import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Tooltip, IconButton, Popover } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  UndoOutlined, RedoOutlined, SaveOutlined,
  CheckCircleOutlined, ErrorOutlined,
  VisibilityOutlined, VisibilityOffOutlined,
  CloseOutlined, SettingsOutlined, OpenInNewOutlined,
  PublicOutlined, LockOutlined, LockOpenOutlined,
} from '@mui/icons-material';
import { api } from '../../../hooks/useApi';
import { PRIMARY, LOBBY_BREAKPOINTS } from './constants';
import { LobbyEngine } from './engine/LobbyEngine';

// ── PreviewCanvas ─────────────────────────────────────────────────────────────
// Renders LobbyEngine once (keyed only to activeLobbyId). Switching breakpoints
// only resizes the container — the engine never remounts.
export function PreviewCanvas({ activeLobbyId, breakpoint }) {
  const wrapRef   = useRef(null);
  const [scale, setScale] = useState(1);
  const bpSpec    = LOBBY_BREAKPOINTS.find(b => b.key === breakpoint) || LOBBY_BREAKPOINTS[0];
  const isDesktop = breakpoint === 'desktop';
  const PADDING   = 24;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const compute = () => {
      if (isDesktop) { setScale(1); return; }
      const availW = el.clientWidth  - PADDING * 2;
      const availH = el.clientHeight - PADDING * 2;
      setScale(Math.min(1, availW / bpSpec.canvasW, availH / bpSpec.canvasH));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [breakpoint, bpSpec.canvasW, bpSpec.canvasH, isDesktop]);

  const radius = breakpoint === 'mobile' ? '28px' : '12px';

  // Desktop: engine fills wrapper naturally (scale=1, no frame, no transform).
  // Tablet/mobile: engine is rendered at true device pixel size then scaled down
  // via CSS transform to fit. The outer sizer reserves the post-scale footprint
  // so the frame stays centered.
  const engineW = isDesktop ? '100%' : `${bpSpec.canvasW}px`;
  const engineH = isDesktop ? '100%' : `${bpSpec.canvasH}px`;

  return (
    <Box
      ref={wrapRef}
      sx={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0e1a' }}
    >
      {/* Sizer: reserves the post-scale footprint */}
      <Box sx={{
        width:      isDesktop ? '100%' : `${bpSpec.canvasW * scale}px`,
        height:     isDesktop ? '100%' : `${bpSpec.canvasH * scale}px`,
        flexShrink: 0,
        position:   'relative',
      }}>
        {/* Frame: true device size, transformed down */}
        <Box sx={{
          position:        isDesktop ? 'static' : 'absolute',
          top: 0, left: 0,
          width:           engineW,
          height:          engineH,
          transform:       isDesktop ? 'none' : `scale(${scale})`,
          transformOrigin: 'top left',
          borderRadius:    isDesktop ? 0 : radius,
          overflow:        'hidden',
          boxShadow:       isDesktop ? 'none' : '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.08)',
        }}>
          <LobbyEngine
            key={activeLobbyId}
            startLobbyId={activeLobbyId}
            breakpoint={breakpoint}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ── LobbySettingsPopover ──────────────────────────────────────────────────────
function LobbySettingsPopover({ anchor, onClose, activeLobby, event, onUpdateLobby, onUpdateEvent }) {
  const [name, setName] = React.useState(activeLobby?.name || '');
  const [slug, setSlug] = React.useState(activeLobby?.slug || '');
  const [slugAvailable, setSlugAvailable] = React.useState(null);
  const [checkingSlug, setCheckingSlug] = React.useState(false);
  const debounceRef = React.useRef(null);

  React.useEffect(() => {
    setName(activeLobby?.name || '');
    setSlug(activeLobby?.slug || '');
    setSlugAvailable(null);
  }, [activeLobby?._id]);

  const checkSlug = React.useCallback((s) => {
    if (!s || !activeLobby?._id || !event?._id) return;
    clearTimeout(debounceRef.current);
    setCheckingSlug(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/lobbies/check-slug', { params: { slug: s, eventId: event._id, excludeId: activeLobby._id } });
        setSlugAvailable(res.data?.available ?? true);
      } catch { setSlugAvailable(null); }
      finally { setCheckingSlug(false); }
    }, 500);
  }, [activeLobby?._id, event?._id]);

  const TogRow = ({ label, description, checked, onChange, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: '1px solid #f0f2f5' }}>
      <Box sx={{ width: 30, height: 30, borderRadius: 1.5, background: checked ? alpha(PRIMARY, 0.1) : '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: checked ? PRIMARY : '#9ea8b3', transition: 'all 0.15s' }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e' }}>{label}</Typography>
        {description && <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3', mt: 0.15, lineHeight: 1.4 }}>{description}</Typography>}
      </Box>
      <Box onClick={() => onChange(!checked)} sx={{ width: 36, height: 20, borderRadius: 10, background: checked ? PRIMARY : '#d1d9e0', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
        <Box sx={{ position: 'absolute', top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
      </Box>
    </Box>
  );

  const SectionLabel = ({ label }) => (
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75, mt: 1.25 }}>{label}</Typography>
  );

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { mt: 1, borderRadius: 2.5, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e8edf2', width: 290, overflow: 'hidden' } } }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1a1a2e' }}>Lobby Settings</Typography>
        <Box onClick={onClose} sx={{ cursor: 'pointer', color: '#9ea8b3', '&:hover': { color: '#1a1a2e' }, display: 'flex', alignItems: 'center' }}><CloseOutlined sx={{ fontSize: 16 }} /></Box>
      </Box>
      <Box sx={{ p: 2, maxHeight: 480, overflowY: 'auto' }}>
        <SectionLabel label="Identity" />
        <Box sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#5a6474', mb: 0.4 }}>Name</Typography>
          <Box sx={{ border: '1.5px solid #e4e9f0', borderRadius: 1.5, background: '#f8f9fb', px: 1.25, '&:focus-within': { borderColor: PRIMARY, background: '#fff' }, transition: 'all 0.15s' }}>
            <Box component="input" value={name} onChange={e => setName(e.target.value)} onBlur={() => { if (name.trim()) onUpdateLobby({ name: name.trim() }); }}
              sx={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', py: 0.75, fontSize: '0.78rem', color: '#1a1a2e', fontFamily: 'inherit' }} />
          </Box>
        </Box>
        <Box sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#5a6474', mb: 0.4 }}>Slug</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', border: '1.5px solid #e4e9f0', borderRadius: 1.5, background: '#f8f9fb', px: 1.25, '&:focus-within': { borderColor: PRIMARY, background: '#fff' }, transition: 'all 0.15s' }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3', mr: 0.25 }}>/</Typography>
            <Box component="input" value={slug}
              onChange={e => { const s = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-'); setSlug(s); setSlugAvailable(null); checkSlug(s); }}
              onBlur={() => { if (slug && slugAvailable !== false) onUpdateLobby({ slug }); }}
              sx={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', py: 0.75, fontSize: '0.78rem', fontFamily: 'monospace', color: '#1a1a2e' }} />
          </Box>
          {checkingSlug && <Typography sx={{ fontSize: '0.62rem', color: '#9ea8b3', mt: 0.35 }}>Checking…</Typography>}
          {!checkingSlug && slugAvailable === false && <Typography sx={{ fontSize: '0.62rem', color: '#ef4444', mt: 0.35 }}>Slug not available</Typography>}
          {!checkingSlug && slugAvailable === true && <Typography sx={{ fontSize: '0.62rem', color: '#22c55e', mt: 0.35 }}>Slug is available ✓</Typography>}
        </Box>
        <SectionLabel label="Visibility" />
        <TogRow label="Published" description="Visible to participants" checked={activeLobby?.isActive !== false} onChange={v => onUpdateLobby({ isActive: v })} icon={<PublicOutlined sx={{ fontSize: 15 }} />} />
        <SectionLabel label="Access" />
        <TogRow label="Require login" description="Participants must log in to access this lobby" checked={activeLobby?.requireLogin || false} onChange={v => onUpdateLobby({ requireLogin: v })} icon={activeLobby?.requireLogin ? <LockOutlined sx={{ fontSize: 15 }} /> : <LockOpenOutlined sx={{ fontSize: 15 }} />} />
      </Box>
    </Popover>
  );
}

// ── BuilderToolbar ────────────────────────────────────────────────────────────
export function BuilderToolbar({
  activeLobby, hist, previewMode, saveStatus, onUndo, onRedo, onSave, onTogglePreview,
  event, onUpdateLobby, onUpdateEvent, visitUrl, onVisit, isWizard,
  breakpoint, onSetBreakpoint,
}) {
  const [settingsAnchor, setSettingsAnchor] = React.useState(null);

  if (isWizard) return null;

  return (
    <Box onClick={e => e.stopPropagation()}
      sx={{ background: '#fff', borderBottom: '1px solid #e8edf2', px: 2, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}
    >
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Tooltip title="Undo"><IconButton size="small" onClick={onUndo} disabled={hist.idx === 0}><UndoOutlined sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        <Tooltip title="Redo"><IconButton size="small" onClick={onRedo} disabled={hist.idx >= hist.stack.length - 1}><RedoOutlined sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        {onSave && (
          <Tooltip title={saveStatus === 'saved' ? 'Saved!' : saveStatus === 'error' ? 'Save failed' : 'Save'}>
            <Box onClick={onSave} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, px: 1.25, height: 30, borderRadius: 1.5, cursor: saveStatus === 'saving' ? 'default' : 'pointer', fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s', ...(saveStatus === 'saved' ? { background: alpha('#22c55e', 0.1), color: '#16a34a', border: '1px solid #86efac' } : saveStatus === 'error' ? { background: alpha('#ef4444', 0.1), color: '#dc2626', border: '1px solid #fca5a5' } : saveStatus === 'saving' ? { background: '#f0f4f8', color: '#9ea8b3', border: '1px solid #e4e9f0' } : { background: '#f0f4f8', color: '#5a6474', border: '1px solid #e4e9f0', '&:hover': { background: alpha(PRIMARY, 0.06), color: PRIMARY, borderColor: alpha(PRIMARY, 0.3) } }) }}>
              {saveStatus === 'saved' ? <CheckCircleOutlined sx={{ fontSize: 14 }} /> : saveStatus === 'error' ? <ErrorOutlined sx={{ fontSize: 14 }} /> : <SaveOutlined sx={{ fontSize: 14 }} />}
              <Box component="span">{saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Error' : 'Save'}</Box>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* ── Breakpoint switcher (center) ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {activeLobby && <>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#3c4555' }}>{activeLobby.name}</Typography>
          {activeLobby.isDefault && <Box sx={{ background: PRIMARY, color: '#fff', fontSize: '0.6rem', fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px', letterSpacing: '0.05em' }}>DEFAULT</Box>}
        </>}

        <Box sx={{ display: 'flex', alignItems: 'center', background: '#f0f4f8', border: '1px solid #e4e9f0', borderRadius: 2, p: '2px', gap: '2px' }}>
          {/* Desktop */}
          {(() => {
            const bpDef = LOBBY_BREAKPOINTS[0];
            const active = breakpoint === bpDef.key;
            return (
              <Tooltip title={bpDef.label} key={bpDef.key}>
                <IconButton size="small" onClick={() => onSetBreakpoint(bpDef.key)}
                  sx={{ width: 32, height: 30, borderRadius: 1.5, color: active ? PRIMARY : '#9ea8b3', background: active ? '#fff' : 'transparent', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                  {bpDef.icon}
                </IconButton>
              </Tooltip>
            );
          })()}

          <Box sx={{ width: '1px', height: 16, background: '#e4e9f0', mx: '1px' }} />

          {/* Tablet landscape + portrait */}
          {LOBBY_BREAKPOINTS.filter(b => b.group === 'tablet').map(bpDef => {
            const active = breakpoint === bpDef.key;
            const hasOverride = activeLobby?.responsive?.[bpDef.key];
            return (
              <Tooltip key={bpDef.key} title={bpDef.label}>
                <Box sx={{ position: 'relative', display: 'flex' }}>
                  <IconButton size="small" onClick={() => onSetBreakpoint(bpDef.key)}
                    sx={{ width: 32, height: 30, borderRadius: 1.5, color: active ? '#b45309' : '#9ea8b3', background: active ? '#fffbeb' : 'transparent', boxShadow: active ? '0 1px 3px rgba(245,158,11,0.2)' : 'none' }}>
                    {bpDef.icon}
                  </IconButton>
                  {hasOverride && <Box sx={{ position: 'absolute', top: 2, right: 2, width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', border: '1px solid #fff', pointerEvents: 'none' }} />}
                </Box>
              </Tooltip>
            );
          })}

          <Box sx={{ width: '1px', height: 16, background: '#e4e9f0', mx: '1px' }} />

          {/* Mobile */}
          {(() => {
            const bpDef = LOBBY_BREAKPOINTS.find(b => b.key === 'mobile');
            const active = breakpoint === bpDef.key;
            const hasOverride = activeLobby?.responsive?.[bpDef.key];
            return (
              <Tooltip title={bpDef.label} key={bpDef.key}>
                <Box sx={{ position: 'relative', display: 'flex' }}>
                  <IconButton size="small" onClick={() => onSetBreakpoint(bpDef.key)}
                    sx={{ width: 32, height: 30, borderRadius: 1.5, color: active ? '#b91c1c' : '#9ea8b3', background: active ? '#fff5f5' : 'transparent', boxShadow: active ? '0 1px 3px rgba(239,68,68,0.2)' : 'none' }}>
                    {bpDef.icon}
                  </IconButton>
                  {hasOverride && <Box sx={{ position: 'absolute', top: 2, right: 2, width: 6, height: 6, borderRadius: '50%', background: '#ef4444', border: '1px solid #fff', pointerEvents: 'none' }} />}
                </Box>
              </Tooltip>
            );
          })()}
        </Box>

        <Typography sx={{ fontSize: '0.68rem', color: '#b0bac8', fontFamily: 'monospace' }}>
          {LOBBY_BREAKPOINTS.find(b => b.key === breakpoint)?.canvasW} × {LOBBY_BREAKPOINTS.find(b => b.key === breakpoint)?.canvasH}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Lobby settings">
          <Box onClick={e => setSettingsAnchor(e.currentTarget)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 2, cursor: 'pointer', color: settingsAnchor ? PRIMARY : '#5a6474', border: `1px solid ${settingsAnchor ? alpha(PRIMARY, 0.4) : '#e8edf2'}`, background: settingsAnchor ? alpha(PRIMARY, 0.06) : 'transparent', transition: 'all 0.15s', '&:hover': { color: PRIMARY, borderColor: alpha(PRIMARY, 0.3) } }}>
            <SettingsOutlined sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>

        <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e8edf2', borderRadius: 2, overflow: 'hidden' }}>
          <Box onClick={onTogglePreview}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', px: 1.5, py: 0.75, transition: 'all 0.15s', ...(previewMode ? { color: '#fff', background: PRIMARY } : { color: '#5a6474', '&:hover': { color: PRIMARY, background: alpha(PRIMARY, 0.04) } }) }}>
            {previewMode ? <VisibilityOffOutlined sx={{ fontSize: 16 }} /> : <VisibilityOutlined sx={{ fontSize: 16 }} />}
            {previewMode ? 'Exit Preview' : 'Preview'}
          </Box>
          {visitUrl && !previewMode && <>
            <Box sx={{ width: '1px', alignSelf: 'stretch', background: '#e8edf2' }} />
            <Tooltip title="Open live lobby in a new tab">
              <Box onClick={onVisit}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', px: 1.5, py: 0.75, transition: 'all 0.15s', color: '#5a6474', '&:hover': { color: PRIMARY, background: alpha(PRIMARY, 0.04) } }}>
                <OpenInNewOutlined sx={{ fontSize: 16 }} />
                Visit Lobby
              </Box>
            </Tooltip>
          </>}
        </Box>
      </Box>

      <LobbySettingsPopover
        anchor={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        activeLobby={activeLobby}
        event={event}
        onUpdateLobby={onUpdateLobby}
        onUpdateEvent={onUpdateEvent}
      />
    </Box>
  );
}
